import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Text,
  Button,
  Textarea,
  Group,
  Stack,
  ActionIcon,
  Divider,
  Avatar,
  Paper,
  Loader,
} from '@mantine/core';
import {
  IconHeart,
  IconHeartFilled,
  IconMessageCircle,
  IconEdit,
  IconTrash,
  IconSend,
} from '@tabler/icons-react';
import { api } from '../apiClient';
import { isAdmin } from '../utils/authUtils';

const formatDate = (dateStr) => {
  const d = new Date(dateStr);
  return d.toLocaleDateString('ja-JP', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

function CommentItem({ comment, questionId, user, isAdminUser, onRefresh, depth }) {
  const [showReplies, setShowReplies] = useState(false);
  const [replies, setReplies] = useState([]);
  const [loadingReplies, setLoadingReplies] = useState(false);
  const [showReplyForm, setShowReplyForm] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [editText, setEditText] = useState(comment.body || '');
  const [likeCount, setLikeCount] = useState(Number(comment.like_count) || 0);
  const [userLiked, setUserLiked] = useState(Boolean(comment.user_liked));
  const [submitting, setSubmitting] = useState(false);

  const currentUserId = user?.userId;
  const userName = user?.signInDetails?.loginId ?? user?.username ?? '';
  const isOwner = currentUserId === comment.user_id;
  const canDelete = isOwner || isAdminUser;

  const loadReplies = useCallback(async () => {
    setLoadingReplies(true);
    try {
      const data = await api.get(`/questions/${questionId}/comments`, { parentId: comment.id });
      setReplies(data);
    } catch (e) {
      console.error('返信取得失敗:', e);
    } finally {
      setLoadingReplies(false);
    }
  }, [questionId, comment.id]);

  const handleToggleReplies = async () => {
    if (!showReplies) {
      await loadReplies();
    }
    setShowReplies((prev) => !prev);
  };

  const handleLike = async () => {
    try {
      const result = await api.post(`/questions/${questionId}/comments/${comment.id}/likes`);
      setUserLiked(result.liked);
      setLikeCount((prev) => (result.liked ? prev + 1 : prev - 1));
    } catch (e) {
      console.error('いいね失敗:', e);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('このコメントを削除しますか？')) return;
    try {
      await api.delete(`/questions/${questionId}/comments/${comment.id}`);
      onRefresh();
    } catch (e) {
      console.error('削除失敗:', e);
    }
  };

  const handleEditSave = async () => {
    if (!editText.trim()) return;
    setSubmitting(true);
    try {
      await api.put(`/questions/${questionId}/comments/${comment.id}`, { body: editText });
      setIsEditing(false);
      onRefresh();
    } finally {
      setSubmitting(false);
    }
  };

  const handleReplySubmit = async () => {
    if (!replyText.trim()) return;
    setSubmitting(true);
    try {
      await api.post(`/questions/${questionId}/comments`, {
        body: replyText,
        parent_comment_id: comment.id,
        user_name: userName,
      });
      setReplyText('');
      setShowReplyForm(false);
      await loadReplies();
      setShowReplies(true);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box pl={depth > 0 ? 'md' : 0}>
      <Paper withBorder p="sm" radius="md" mb={4}>
        <Group align="flex-start" gap="xs">
          <Avatar size="sm" radius="xl" color="blue">
            {comment.is_deleted && !isAdminUser
              ? '?'
              : (comment.user_name || '?').charAt(0).toUpperCase()}
          </Avatar>
          <Box style={{ flex: 1, minWidth: 0 }}>
            <Group gap="xs" align="center">
              <Text fw={600} size="sm">
                {comment.is_deleted && !isAdminUser ? 'Unknown' : comment.user_name}
              </Text>
              <Text size="xs" c="dimmed">
                {formatDate(comment.created_at)}
              </Text>
            </Group>

            {comment.is_deleted ? (
              <Text size="sm" c="dimmed" fs="italic" mt={2}>
                削除済みコメント
              </Text>
            ) : isEditing ? (
              <Box mt="xs">
                <Textarea
                  value={editText}
                  onChange={(e) => setEditText(e.target.value)}
                  autosize
                  minRows={2}
                />
                <Group mt="xs" gap="xs">
                  <Button size="xs" onClick={handleEditSave} loading={submitting}>
                    保存
                  </Button>
                  <Button
                    size="xs"
                    variant="default"
                    onClick={() => {
                      setIsEditing(false);
                      setEditText(comment.body || '');
                    }}
                  >
                    キャンセル
                  </Button>
                </Group>
              </Box>
            ) : (
              <Text size="sm" mt={2} style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                {comment.body}
              </Text>
            )}

            <Group mt="xs" gap={4}>
              <ActionIcon
                variant={userLiked ? 'filled' : 'subtle'}
                color={userLiked ? 'red' : 'gray'}
                size="sm"
                onClick={handleLike}
              >
                {userLiked ? <IconHeartFilled size={14} /> : <IconHeart size={14} />}
              </ActionIcon>
              <Text size="xs" c="dimmed" mr="xs">
                {likeCount}
              </Text>

              {depth === 0 && (
                <Button
                  variant="subtle"
                  size="xs"
                  color="gray"
                  leftSection={<IconMessageCircle size={12} />}
                  onClick={() => setShowReplyForm((prev) => !prev)}
                >
                  返信
                </Button>
              )}

              {!comment.is_deleted && isOwner && (
                <ActionIcon
                  variant="subtle"
                  color="gray"
                  size="sm"
                  onClick={() => {
                    setIsEditing(true);
                    setEditText(comment.body || '');
                  }}
                >
                  <IconEdit size={14} />
                </ActionIcon>
              )}
              {!comment.is_deleted && canDelete && (
                <ActionIcon variant="subtle" color="red" size="sm" onClick={handleDelete}>
                  <IconTrash size={14} />
                </ActionIcon>
              )}
            </Group>

            {showReplyForm && (
              <Box mt="xs">
                <Textarea
                  placeholder="返信を入力..."
                  value={replyText}
                  onChange={(e) => setReplyText(e.target.value)}
                  autosize
                  minRows={2}
                />
                <Group mt="xs" gap="xs">
                  <Button
                    size="xs"
                    leftSection={<IconSend size={12} />}
                    onClick={handleReplySubmit}
                    loading={submitting}
                    disabled={!replyText.trim()}
                  >
                    返信する
                  </Button>
                  <Button
                    size="xs"
                    variant="default"
                    onClick={() => {
                      setShowReplyForm(false);
                      setReplyText('');
                    }}
                  >
                    キャンセル
                  </Button>
                </Group>
              </Box>
            )}
          </Box>
        </Group>
      </Paper>

      {depth === 0 && (comment.reply_count > 0 || replies.length > 0) && (
        <Box pl="lg" mb="xs">
          <Button
            variant="subtle"
            size="xs"
            color="blue"
            leftSection={<IconMessageCircle size={12} />}
            onClick={handleToggleReplies}
            loading={loadingReplies}
          >
            {showReplies ? '返信を非表示' : `${Math.max(comment.reply_count, replies.length)}件の返信を表示`}
          </Button>

          {showReplies && (
            <Box mt="xs">
              {replies.map((reply) => (
                <CommentItem
                  key={reply.id}
                  comment={reply}
                  questionId={questionId}
                  user={user}
                  isAdminUser={isAdminUser}
                  onRefresh={loadReplies}
                  depth={1}
                />
              ))}
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
}

function QuestionComments({ questionId, user }) {
  const [comments, setComments] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [isAdminUser, setIsAdminUser] = useState(false);

  const LIMIT = 10;
  const userName = user?.signInDetails?.loginId ?? user?.username ?? '';

  useEffect(() => {
    isAdmin(user).then(setIsAdminUser);
  }, [user]);

  const loadComments = useCallback(
    async (targetPage, append) => {
      setLoading(true);
      try {
        const data = await api.get(`/questions/${questionId}/comments`, {
          page: targetPage,
          limit: LIMIT,
        });
        if (append) {
          setComments((prev) => [...prev, ...data.comments]);
        } else {
          setComments(data.comments);
        }
        setTotal(data.total);
        setPage(targetPage);
        setHasMore(data.hasMore);
      } catch (e) {
        console.error('コメント取得失敗:', e);
      } finally {
        setLoading(false);
      }
    },
    [questionId]
  );

  useEffect(() => {
    setComments([]);
    setPage(1);
    setHasMore(false);
    loadComments(1, false);
  }, [questionId, loadComments]);

  const handleSubmit = async () => {
    if (!newComment.trim()) return;
    setSubmitting(true);
    try {
      await api.post(`/questions/${questionId}/comments`, {
        body: newComment,
        user_name: userName,
      });
      setNewComment('');
      await loadComments(1, false);
    } finally {
      setSubmitting(false);
    }
  };

  const handleLoadMore = () => {
    loadComments(page + 1, true);
  };

  return (
    <Box mt="xl" pb="xl">
      <Divider mb="md" label="コメント" labelPosition="left" />

      <Box mb="lg">
        <Textarea
          placeholder="コメントを入力..."
          value={newComment}
          onChange={(e) => setNewComment(e.target.value)}
          autosize
          minRows={2}
        />
        <Group mt="xs" justify="flex-end">
          <Button
            size="sm"
            leftSection={<IconSend size={14} />}
            onClick={handleSubmit}
            loading={submitting}
            disabled={!newComment.trim()}
          >
            コメントする
          </Button>
        </Group>
      </Box>

      {loading && page === 1 ? (
        <Group justify="center" py="md">
          <Loader size="sm" />
        </Group>
      ) : (
        <>
          <Text size="sm" c="dimmed" mb="sm">
            {total}件のコメント
          </Text>

          <Stack gap="xs">
            {comments
              .filter(comment => !(comment.is_deleted && comment.reply_count === 0))
              .map((comment) => (
              <CommentItem
                key={comment.id}
                comment={comment}
                questionId={questionId}
                user={user}
                isAdminUser={isAdminUser}
                onRefresh={() => loadComments(1, false)}
                depth={0}
              />
            ))}
          </Stack>

          {hasMore && (
            <Group justify="center" mt="md">
              <Button variant="default" size="sm" onClick={handleLoadMore} loading={loading}>
                もっと見る
              </Button>
            </Group>
          )}

          {comments.length === 0 && !loading && (
            <Text c="dimmed" ta="center" py="md" size="sm">
              コメントはまだありません。最初のコメントを投稿しましょう！
            </Text>
          )}
        </>
      )}
    </Box>
  );
}

export default QuestionComments;
